<template>
  <div class="home-page flex-col">
    <!-- Hero Section -->
    <section class="hero"
      @mouseenter="pauseCarousel"
      @mouseleave="startCarousel"
    >
      <!-- 动态背景层 -->
      <div class="hero-bg-container">
        <transition-group name="fade-bg" tag="div" class="bg-layer">
          <div
            v-for="(slide, index) in slides"
            v-show="index === currentIndex"
            :key="'bg-' + slide.id"
            class="hero-bg"
            :style="{ backgroundImage: `url(${slide.resolvedBg})` }"
          ></div>
        </transition-group>
      </div>

      <!-- 暗色渐变遮罩 -->
      <div class="hero-overlay"></div>

      <!-- 内容层容器 -->
      <div class="hero-content-container">
        <transition-group name="fade-content" tag="div" class="carousel-inner">
          <div 
            v-for="(slide, index) in slides" 
            v-show="index === currentIndex"
            :key="'content-' + slide.id"
            class="carousel-item"
            :class="{ 'is-clickable': slide.type === 'news' }"
            @click="slide.type === 'news' && navigateToArticle(slide.id)"
          >
            <!-- 品牌 Slogan 内容 (type === 'brand') -->
            <div v-if="slide.type === 'brand'" class="hero-static-content">
              <div class="title1">
                <p>INTELLIGENCE-POWERED GROWTH</p>
                <p>STEER TOWARD THE FUTURE</p>
              </div>
              <div class="title2">
                <p>Your Strategic Partner for Global Expansion & AI Transformation</p>
              </div>
            </div>

            <!-- 新闻内容 (type === 'news') -->
            <div v-else-if="slide.type === 'news'" class="carousel-inner-content">
              <h1 class="carousel-title">{{ slide.title }}</h1>
              <p class="carousel-desc">{{ slide.desc }}</p>
            </div>
          </div>
        </transition-group>

        <!-- 底部轮播指示器 (小横条)：只有在 slides 数量多于 1 时显示 -->
        <div class="carousel-indicators" v-if="slides.length > 1">
          <span 
            v-for="(item, index) in slides"
            :key="'dot-' + index" 
            :class="{ active: index === currentIndex }"
            @click.stop="currentIndex = index"
          ></span>
        </div>
      </div>
    </section>

    <!-- Problems Section -->
    <section class="problems">
      <div class="container">
        <div class="problems-content">
          <h2>We're here to solve your problems.</h2>
          <div class="problems-flex-container">
            <div class="problem-list-column"> 
              <div class="problem-list">
                <div class="problem-item">
                  <span class="checkmark"><img src="../assets/无国界切图/icon/gou.svg"></span>
                  <p> Policy Compliance Adaptation & Implementation Planning
                  </p>
                </div>
                <div class="problem-item">
                  <span class="checkmark"><img src="../assets/无国界切图/icon/gou.svg"></span>
                  <p style="white-space: nowrap;">Localized AI Ecosystem Development & Resource Connection
                  </p>
                </div>
                <div class="problem-item">
                  <span class="checkmark"><img src="../assets/无国界切图/icon/gou.svg"></span>
                  <p>AI Talent Pipeline Building & Skill Empowerment
                  </p>
                </div>
                <div class="problem-item">
                  <span class="checkmark"><img src="../assets/无国界切图/icon/gou.svg"></span>
                  <p>Customized AI Top-Level Design Consulting
                  </p>
                </div>
                <div class="problem-item">
                  <span class="checkmark"><img src="../assets/无国界切图/icon/gou.svg"></span>
                  <p>Industry AI Transformation Solution Delivery
                  </p>
                </div>
                <div class="problem-item">
                  <span class="checkmark"><img src="../assets/无国界切图/icon/gou.svg"></span>
                  <p> Cross-Regional AI Layout Strategic Planning
                  </p>
                </div>
                <div class="problem-item">
                  <span class="checkmark"><img src="../assets/无国界切图/icon/gou.svg"></span>
                  <p>Digital & Intelligent Upgrade Path Design
                  </p>
                </div>
                <div class="problem-item">
                  <span class="checkmark"><img src="../assets/无国界切图/icon/gou.svg"></span>
                  <p>End-to-End AI Project Implementation Support
                  </p>
                </div>
              </div>
              <p></p>
              <p></p>
              <div class="problem-description"><p>We understand that introducing AI in developing regions presents multiple challenges: policy gaps, weak ecosystems, talent shortages, and implementation complexity. We provide comprehensive support from strategy design to ground-level execution.</p></div>
            </div>
            <div class="problem-image-column">
              
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Featured Engagement -->
    <section class="featured-engagement">
      <div class="container">
        <div class="engagement-content">
          <h2>Why AI Without Borders?</h2>
          <h3>Dual-Track Driven, Deep Collaboration 
            <span class="tooltip">
              <span class="tooltip-text">Our unique dual-track service model—integrating AI and Global Governance—delivers seamless alignment between technology and strategy for you.</span>
            </span>
          </h3>
          <h3>End-to-End Enablement, Beyond Just Recommendations 
            <span class="tooltip">
              <span class="tooltip-text">We partner with clients from 0 to 1, jointly driving the implementation of AI technologies and the execution of risk response strategies.</span>
            </span>
          </h3>
          <h3>Global Perspective, Forward-Thinking Insights 
            <span class="tooltip">
              <span class="tooltip-text">Backed by our analytical capabilities rooted in multi-dimensional models, we keep you one step ahead—empowering you to seize opportunities amid global shifts.</span>
            </span>
          </h3>
        </div>
      </div>
    </section>

    <section class="events">
      <div class="container">
        <h2>Events</h2>
        <div class="event-list">
          <div 
            class="event-card" 
            v-for="article in articles" 
            :key="article.id"
            @click="navigateToArticle(article.id)"
          >
            <div class="event-img">
              <img :src="getImage(article.imageSrc) || require('@/assets/images/event_card_grassland.png')" :alt="article.title">
            </div>
            <div class="event-details">
              <h3>{{ article.title }}</h3>
              <p class="event-date">{{ article.date }}</p>
              <p class="event-desc">{{ article.desc }}</p>
            </div>
          </div>
        </div>
        <div class="show-more-wrapper">
          <button class="show-more-btn"
        @click="$router.push('/events')">SHOW MORE</button>
        </div>
      </div>
    </section>

  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'HomePage',
  data() {
    return {
      articles: [],
      // 初始化轮播数组，永远将地球背景放在第一位
      slides: [
        {
          type:'brand',
          id: 'brand',
          resolvedBg: require('@/assets/无国界切图/首页背景1.png')
        }
      ],
      currentIndex:0,
      carouselTimer:null,
      defaultHeroBgImage : require('@/assets/无国界切图/首页背景1.png')
    };
  },

  mounted() {
    axios.get('/api/events')
      .then(response => {
        let fetchedArticles = response.data;
        // 安全检查：确保是数组才处理
        if (typeof fetchedArticles === 'string') {
          try { fetchedArticles = JSON.parse(fetchedArticles); } catch(e) { fetchedArticles = []; }
        }
        if (!Array.isArray(fetchedArticles)) {
          fetchedArticles = [];
        }
        // 按创建时间精确到毫秒倒序排序（过滤掉 'article-' 前缀）
        fetchedArticles.sort((a, b) => {
          let timeA = parseInt((a.id || '').replace(/[^0-9]/g, '')) || 0;
          let timeB = parseInt((b.id || '').replace(/[^0-9]/g, '')) || 0;
          return timeB - timeA;
        });
        // 只截取最前面的 3 篇
        this.articles = fetchedArticles.slice(0, 3);

        // 【关键】将截取到的新闻拼接到地球幻灯片后面
        const newsSlides=this.articles.map(article=>{
          return {
            type:'news',
            ...article,
            resolvedBg:this.getImage(article.imageSrc)|| this.defaultHeroBgImage
          };
        });
        this.slides = [
          { type:'brand',id:'brand',resolvedBg:this.defaultHeroBgImage},
          ...newsSlides
        ];
        this.startCarousel();
      })
      .catch(error => {
        console.error('获取新闻列表失败:', error);
      });
  },
   // 在组件卸载时清除定时器，避免内存泄漏
  beforeUnmount(){
    this.pauseCarousel();
  },
  methods: {
    startCarousel(){
       // 只有地球一张图就不轮播
      if (this.slides.length<=1) return;
      this.pauseCarousel(); // 确保不重复启动定时器
      this.carouselTimer=setInterval(()=>{
        this.currentIndex=(this.currentIndex+1)%this.slides.length;
      }, 2000);
    },
    pauseCarousel(){
      if(this.carouselTimer){
        clearInterval(this.carouselTimer);
        this.carouselTimer=null;
      }
    },
    navigateToArticle(id) {
      this.$router.push(`/article/${id}`);
    },
    getImage(srcString){
      if (!srcString) return '';
      if (srcString.startsWith('/api/') || srcString.startsWith('http')) {
        return srcString;
      }
      const fileName = srcString.split('/').pop();
      try {
        return require(`@/assets/images/${fileName}`);
      } catch(e) {
        return srcString;
      }
    }
  }
}
</script>

<style scoped>
.home-page {
  background-color: rgb(2, 2, 2);
  position: relative;
  width: 100%;
  height:auto;
  min-height: 100vh;
  overflow-x: hidden;
}



.hero {
  color: white;
  padding: 0;
  text-align: center;
  position: relative;
  width: 100%;
  height: auto;
  aspect-ratio: 3840 / 2000;
  display: flex;
  overflow: hidden; /* 防止内部图片溢出 */
}

/* 取消原来的 background-image 设置和原有的 ::before，交由内部组件控制 */
.hero::before {
  display: none;
}

.hero-bg-container, .hero-content-container {
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
}

.hero-bg-container { z-index: 0; }

.hero-bg {
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  background-size: cover;
  background-position: center center;
  background-repeat: no-repeat;
  will-change: opacity;
}

.bg-layer {
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  width: 100%;
  height: 100%;
}

/* 遮罩层，让新闻文字易读，对地球也有一点压暗保护作用 */
.hero-overlay {
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  background: linear-gradient(180deg, rgba(0,0,0,0.0) 0%, rgba(0,0,0,0.5) 100%);
  z-index: 1;
  pointer-events: none;
}

.hero-content-container { z-index: 2; }

/* 每一张幻灯片的容器 */
.carousel-item {
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  width: 100%;
  height: 100%;
}
.carousel-item.is-clickable {
  cursor: pointer;
}

.carousel-inner {
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  width: 100%;
  height: 100%;
}

.hero-static-content {
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  width: 100%;
  height: 100%;
  text-align: left;
}

/* 新闻专属的内容居中样式 */
.carousel-inner-content {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 80%;
  max-width: 1200px;
  text-align: center;
}


@font-face {
  font-family: 'Alibaba-PuHuiTi-Heavy';
  src: url('@/Alibaba-PuHuiTi-Heavy.ttf');
}
.title1 {
  font-family: 'Alibaba-PuHuiTi-Heavy', PingFangSC, sans-serif;
  font-size: 70px;
  font-weight: 800;
  width: 1411px;
  height: 156px;
  color: #FFFFFF;
  line-height: 70px;
  text-shadow: 0px 3px 6px rgba(0,0,0,0.3);
  text-align: left;
  font-style: normal;
  text-transform: uppercase;
  margin-left: 200px;
  margin-top: 300px;
}

.title2 {
  font-family: PingFangSC, PingFang SC, sans-serif;
  font-weight: 500;
  font-size: 22px;
  width: 483px;
  height: 80px;
  color: #d4d2d2;
  line-height: 40px;
  text-align: left;
  font-style: normal;
  position: absolute;
  bottom: 150px;
  left: 200px;
}

.carousel-title {
  font-family: 'Alibaba-PuHuiTi-Heavy', PingFangSC, sans-serif;
  font-size: 56px;
  font-weight: 800;
  color: #FFFFFF;
  line-height: 1.3;
  margin-bottom: 20px;
  text-shadow: 0px 4px 10px rgba(0,0,0,0.6);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  transition: opacity 0.3s ease;
}

.carousel-item:hover .carousel-title {
  opacity: 0.8; /* 点击反馈 */
}

.carousel-desc {
  font-family: PingFangSC, sans-serif;
  font-size: 22px;
  color: #E2E8F0;
  line-height: 1.6;
  max-width: 900px;
  margin: 0 auto;
  text-shadow: 0px 2px 4px rgba(0,0,0,0.6);
  display: -webkit-box;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* 轮播指示器：贴近左下方，与 Slogan 的排版对齐 */
.carousel-indicators {
  position: absolute;
  bottom: 80px;  /* 距离底部高度，可微调 */
  left: 200px;   /* 与原来 title1 的 margin-left 保持对齐一致 */
  display: flex;
  gap: 12px;
  z-index: 3;
}

.carousel-indicators span {
  display: block;
  width: 40px;
  height: 4px;
  background: rgba(255, 255, 255, 0.3);
  cursor: pointer;
  transition: background 0.3s ease;
  border-radius: 2px;
}

.carousel-indicators span.active {
  background: #ffffff;
}

/* ============ CSS 动画 ============ */
.fade-bg-enter-active, .fade-bg-leave-active {
  transition: opacity 0.8s ease;
}
.fade-bg-enter-from, .fade-bg-leave-to {
  opacity: 0;
}

.fade-content-enter-active, .fade-content-leave-active {
  transition: opacity 0.8s ease;
}
.fade-content-enter-from, .fade-content-leave-to {
  opacity: 0;
}

.section {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4rem;
  margin-top: 70px;
  
  flex-wrap: wrap;
}

.section1-flex {
  display: flex;
  align-items: center;
  gap: 25px;
}

.section1-flex img {
  width: 28px;
  height: 28px;
  object-fit: contain;
}

.hero-box {
  width: 100%;
  max-width: 2000px;
  margin: 0 auto;
  text-align: left;
}

.hero-box-flex{
  margin-top: 50px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 30px;
  
}

.hero-text1, .hero-text2 {

  width: 1400px;
  font-family: PingFangSC, PingFang SC;
  font-weight: 400;
  font-size: 30px;
  color: #FFFFFF;
  text-align: center;
  
  line-height: 1.6;
}

.hero-description {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 400px;
  margin-top: 60px;
  width: 100%;
  text-align: center;
}

.service-item {
  width: 100%;
  max-width: 340px;
}

.service-image img {
  width: 80px;
  height: auto;
  border-radius: 8px;
}

.problems {
  color: white;
  padding: 6rem 0;
  background-image: url("../assets/无国界切图/首页背景2@2x.png");
  background-size: cover;
  background-position: center;
}

.problems-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2rem;
  width: 100%;
}

.problems h2 {
  width: 100%;
  max-width: 800px;
  font-family: PingFangSC, PingFang SC;
  font-weight: 600;
  font-size: 3rem;
  color: #FFFFFF;
  line-height: 1.2;
  text-align: left;
  margin-top: 2rem;
}

.problems-flex-container {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 4rem;
  margin-top: 2rem;
  width: 100%;
  flex-wrap: wrap;
}

.problem-list-column {
  flex: 1;
  min-width: 380px;
}

.problem-list {
  list-style: none;
  padding-left: 0;
  font-family: PingFangSC, PingFang SC;
  font-weight: 600;
  font-size: 1.1rem;
  color: #FFFFFF;
  line-height: 1.5;
  text-align: left;
}

.problem-item {
  display: flex;
  align-items: flex-start;
  margin-bottom: 1rem;
}

.problem-description {
  width: 860px;
  max-width: 1500px;
  font-family: LucidaGrande, LucidaGrande;
  font-weight: bold;
  font-size: 1.1rem;
  color: #FFFFFF;
  line-height: 1.5;
  text-align: left;
  margin-top: 2rem;
}

.checkmark {
  color: #4CFA6F;
  margin-right: 1rem;
  font-size: 1.2rem;
  flex-shrink: 0;
}

.problem-image-column {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

.featured-engagement {
  background-image: url('/src/assets/images/backend2.jpeg');
  background-size: cover;
  background-position: center;
  color: black;
  padding: 6rem 0;
  width: 100%;
}

.engagement-content {
  max-width: 800px;
  margin: 0 auto;
  text-align: center;
}

.featured-engagement h2 {
  font-family: PingFangSC, PingFang SC;
  font-weight: 600;
  font-size: 3rem;
  color: #FFFFFF;
  line-height: 1.2;
  margin-bottom: 2rem;
}

.featured-engagement h3 {
  margin-bottom: 2rem;
  font-family: LucidaGrande, LucidaGrande;
  font-weight: bold;
  font-size: 1.1rem;
  color: #FFFFFF;
  line-height: 1.5;
  position: relative;
  display: inline-block;
}

/* Tooltip styles can remain or be adjusted if needed */
.tooltip .tooltip-text {
  /* Ensure tooltip is responsive if necessary */
  max-width: 300px;
  white-space: normal;
}

@media (max-width: 1024px) {
  .problems h2, .featured-engagement h2 {
    font-size: 2.5rem;
  }
  
  .section {
    gap: 2rem;
  }
}

@media (max-width: 768px) {
  .hero {
    padding: 0;
    aspect-ratio: auto;
    height: 450px;
  }

  .title1 {
    font-size: 28px;
    line-height: 1.3;
    width: auto;
    height: auto;
    margin-left: 24px;
    margin-top: 100px;
    padding-right: 24px;
  }

  .title2 {
    font-size: 15px;
    line-height: 1.4;
    width: calc(100% - 48px);
    height: auto;
    left: 24px;
    bottom: 90px;
  }

  .carousel-title {
    font-size: 26px;
    line-clamp: 2;
  }

  .carousel-desc {
    font-size: 14px;
    line-clamp: 3;
  }

  .carousel-indicators {
    left: 50%;
    transform: translateX(-50%);
    bottom: 30px;
  }

  .title-1 img {
    margin-top: 1rem;
  }

  .problems h2, .featured-engagement h2 {
    font-size: 2rem;
  }
  
  .problems-flex-container {
    flex-direction: column;
    gap: 2rem;
  }
  
  .problem-list-column {
    width: 100%;
  }
  
  .hero-text1, .hero-text2 {
    font-size: 1rem;
  }
}

/* ==========================================
   Events 新增板块样式 (精准适配设计图)
   ========================================== */
.events {
  background-color: #0b0d1e; /* 极深蓝黑色背景 */
  padding: 5rem 0 6rem 0;
  width: 100%;
}

.events h2 {
  font-family: PingFangSC, PingFang SC;
  font-weight: 500;
  font-size: 2.2rem;
  color: #FFFFFF;
  margin-bottom: 2.5rem;
  text-align: left;
}

.event-list {
  display: flex;
  justify-content: space-between;
  gap: 2.5rem;
}

.event-card {
  flex: 1;
  display: flex;
  flex-direction: column;
  cursor: pointer;
  transition: transform 0.25s ease;
}

.event-card:hover {
  transform: translateY(-4px);
}

.event-img img {
  width: 100%;
  height: auto;
  aspect-ratio: 16 / 9; /* 16:9 比例风景图 */
  object-fit: cover;
  border-radius: 4px;
}

.event-details {
  margin-top: 1.5rem;
  text-align: left;
}

.event-details h3 {
  font-family: PingFangSC, PingFang SC;
  font-weight: 500;
  font-size: 1.15rem;
  color: #ffffff;
  line-height: 1.4;
  margin-bottom: 0.4rem;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.event-date {
  font-family: PingFangSC, PingFang SC;
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.4); /* 半透明白色，小字 */
  margin-bottom: 0.8rem;
}

.event-desc {
  font-family: PingFangSC, PingFang SC;
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.7); /* 半透明内容文本 */
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.show-more-wrapper {
  margin-top: 3.5rem;
  display: flex;
  justify-content: center;
}

.show-more-btn {
  background-color: transparent;
  color: #ffffff;
  border: none;
  border-bottom: 1px solid #ffffff;
  padding: 0 0 4px 0;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  letter-spacing: 0.5px;
  transition: opacity 0.3s ease;
}

.show-more-btn:hover {
  opacity: 0.75;
}

/* 适配移动端 */
@media (max-width: 768px) {
  .event-list {
    flex-direction: column;
    gap: 3rem;
  }
}
</style>